import React, { useEffect } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Keyboard, Virtual, Pagination } from 'swiper/modules'
import SwiperCore from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import LazyVideoPlayer from '@/components/common/LazyVideoPlayer'
import VideoThumbnail from '@/components/common/VideoThumbnail'

interface VideoItem {
  id: string
  url: string
  thumbnail?: string
  title?: string
  startTime?: number
  endTime?: number
  isAudio?: boolean
}

interface VideoCarouselExpandedProps {
  videos: VideoItem[]
  selectedVideoIndex: number
  setSelectedVideoIndex: (index: number) => void
  handlePrevVideo?: () => void
  handleNextVideo?: () => void
  isLoading?: boolean
}

const VideoCarouselExpanded = ({
  videos,
  selectedVideoIndex,
  setSelectedVideoIndex,
  handlePrevVideo,
  handleNextVideo,
  isLoading = false,
}: VideoCarouselExpandedProps) => {
  // Preload the next video when the current one is selected
  useEffect(() => {
    if (videos.length <= 1 || selectedVideoIndex >= videos.length - 1) return

    // Just create the image element to trigger browser preloading
    const nextIndex = selectedVideoIndex + 1
    if (videos[nextIndex]?.thumbnail) {
      const img = new Image()
      img.src = videos[nextIndex].thumbnail || ''
    }
  }, [selectedVideoIndex, videos])

  if (isLoading) {
    return (
      <div className="py-4">
        <h3 className="font-manrope text-lg font-semibold text-[#40424A] mb-4">User Requests</h3>
        <div className="aspect-video w-full bg-gray-100 rounded-lg animate-pulse flex items-center justify-center">
          <div className="text-gray-500">Loading videos...</div>
        </div>
      </div>
    )
  }

  if (videos.length === 0) {
    return (
      <div className="py-4">
        <h3 className="font-manrope text-lg font-semibold text-[#40424A] mb-4">User Requests</h3>
        <div className="aspect-video w-full bg-gray-100 rounded-lg flex items-center justify-center">
          <div className="text-gray-500">No videos available</div>
        </div>
      </div>
    )
  }

  // Handler for slide changes
  const handleSlideChange = (swiper: SwiperCore) => {
    setSelectedVideoIndex(swiper.activeIndex)
    // Also notify the parent component if handlers are provided
    if (swiper.activeIndex < selectedVideoIndex && handlePrevVideo) {
      handlePrevVideo()
    } else if (swiper.activeIndex > selectedVideoIndex && handleNextVideo) {
      handleNextVideo()
    }
  }

  return (
    <div className="py-4">
      <h3 className="font-manrope text-lg font-semibold text-[#40424A] mb-4">User Requests</h3>
      <Swiper
        className="main-swiper"
        style={{ borderRadius: '8px' }}
        slidesPerView={1}
        navigation={true}
        pagination={{
          clickable: true,
          // Customize pagination to match our brand color
          bulletActiveClass: 'swiper-pagination-bullet-active custom-bullet-active',
        }}
        keyboard={{
          enabled: true,
        }}
        virtual
        modules={[Navigation, Keyboard, Virtual, Pagination]}
        onSlideChange={handleSlideChange}
      >
        {videos.map((video, index) => (
          <SwiperSlide key={video.id || index} virtualIndex={index}>
            <div className="aspect-video w-full rounded-lg overflow-hidden">
              {index === selectedVideoIndex ? (
                <LazyVideoPlayer
                  videoUrl={video.url}
                  startTime={video.startTime}
                  endTime={video.endTime}
                  poster={video.thumbnail}
                  isAudio={video.isAudio}
                />
              ) : (
                <VideoThumbnail
                  src={video.thumbnail || ''}
                  alt={video.title || 'Video thumbnail'}
                  className="w-full h-full"
                  isAudio={video.isAudio}
                />
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Removed custom pagination dots as Swiper already provides pagination */}
    </div>
  )
}

export default VideoCarouselExpanded
